// @flow

import _ from 'lodash';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router';
import cx from 'classnames';
import React, { Component, type Node } from 'react';
import styled from 'styled-components';

import { BREADCRUMB_CLICK } from 'client/constants/amplitudeTracker';
import { save as saveUser } from 'client/actions/user';
import COLORS from 'client/constants/colors';
import CONTENTFUL_ENTRY_ID from 'client/constants/contentfulEntry';
import IndicatorWrapper from 'client/components/indicatorWrapper';
import Nav from 'client/components/nav/nav';
import ROUTES from 'client/constants/routes';
import StrideHeader from 'client/components/strideHeader';
import Translate from 'client/components/translate';
import 'client/styles/tabs.scss';
import 'client/styles/breadcrumbs.scss';

import { TRANSLATION_SWITCH_PATHS } from 'client/constants/health';

import ContentfulService from 'web-core/src/contentful-service';
import { Tracking } from 'web-core/src/tracking';
import type { UserType } from 'web-core/types/user';

const BreadcrumbLink = styled(Link)`
  margin-left: 0.5625rem;
  position: relative;
  vertical-align: middle;
  z-index: 2;

  :active,
  :focus,
  :link,
  :visited {
    color: ${COLORS.WHITE};
  }

  :hover {
    color: ${COLORS.WHITE};
    font-weight: bold;
  }
`;

type BreadcrumbProps = {
  active: boolean,
  allowNavigation: boolean,
  id: string,
  index: number,
  last?: boolean,
  linkName: string,
  parentPath: string,
  visited: boolean,
};

function Breadcrumb({
  active,
  allowNavigation,
  id,
  index,
  last,
  linkName,
  parentPath,
  visited,
}: BreadcrumbProps) {
  const stepNumber = index + 1;
  const sectionRoute = `${parentPath}/${id}`;

  return (
    <div className={cx('breadcrumb', { active, visited, last, first: index === 0 })} id={id}>
      <div className="label-wrapper">
        {allowNavigation ? (
          <BreadcrumbLink
            to={sectionRoute}
            onClick={() => Tracking.track(BREADCRUMB_CLICK, { sectionRoute })}>
            {active ? <Translate path={linkName} /> : stepNumber}
          </BreadcrumbLink>
        ) : (
          <span className="label">{active ? <Translate path={linkName} /> : index + 1}</span>
        )}
      </div>
    </div>
  );
}

Breadcrumb.defaultProps = {
  allowNavigation: false,
  last: false,
  parentPath: '',
};

const IndicatorWrapperWithRouter = withRouter(IndicatorWrapper);
const BC_OUTER_ID = 'bc-outer';

function getWidth(id) {
  const element = document.getElementById(id);
  return element && element.offsetWidth;
}

type State = {
  phoneWhitelistMap: {
    [key: string]: boolean,
  },
};

type Props = {
  brandName: string,
  children: Node,
  location: {
    basename: string,
    pathname: string,
  },
  route: {
    allowNavigation: boolean,
    className: string,
    parentPath?: string,
    steps: Array<string>,
  },
  saveUser: UserType => Promise<*>,
  stepData: Object,
  user: UserType,
};

/*
We only want to show our support number to members or customers with high intent
to purchase. Commented out paths are paths that were originally requested to
remain visible, keeping them in the event that we want to add the phone number back
*/

const visionPhonePurchaseWhitelistPaths = [ROUTES.VISION_ESIGN, ROUTES.VISION_COMPLETE];

const edePhoneWhitelistPaths = [ROUTES.EDE_ELIGIBILITY_RESULTS, ROUTES.EDE_PLAN];

const mergedPhoneWhitelistPaths = [...visionPhonePurchaseWhitelistPaths, ...edePhoneWhitelistPaths];

/* MX can add the query params ?es or ?en to initialize a page in a certain language.
 * Remove when a user makes a selection.
 */
const getUrlWithoutLanguageCode = () => {  
  const queryParams = new URLSearchParams(window.location.search);
  queryParams.delete('es');
  queryParams.delete('en');

  return `${window.location.origin}${window.location.pathname}?${queryParams.toString()}`;
};

class App extends Component<Props, State> {
  state = {
    phoneWhitelistMap: {
      [ROUTES.VISION_ESIGN]: false,
      [ROUTES.VISION_COMPLETE]: false,
      [ROUTES.EDE_ELIGIBILITY_RESULTS]: false,
      [ROUTES.EDE_PLAN]: false,
    },
  };

  componentDidMount() {
    new ContentfulService().getEntryById(CONTENTFUL_ENTRY_ID.MX_PHONE_NUMBER).then((data) => {
      const mxPhoneConfig = _.get(data, 'data.config', {});
      const clonedPhoneWhitelistMap = Object.assign({}, this.state.phoneWhitelistMap);
      _.forEach(edePhoneWhitelistPaths, (path) => {
        clonedPhoneWhitelistMap[path] = mxPhoneConfig.edePhone;
      });
      _.forEach(visionPhonePurchaseWhitelistPaths, (path) => {
        clonedPhoneWhitelistMap[path] = mxPhoneConfig.visionPurchasePhone;
      });
      this.setState({
        phoneWhitelistMap: clonedPhoneWhitelistMap,
      });
    });
  }

  shouldComponentUpdate(nextProps) {
    if (this.props.user.locale !== nextProps.user.locale) {
      return false;
    }

    return true;
  }

  handleTranslationSwitch = () => {
    const { user } = this.props;
    const newUser = Object.assign({}, user);

    const reload = () => {
      window.location.replace(getUrlWithoutLanguageCode());
    };

    if (user.locale === 'en-US') {
      newUser.locale = 'es-US';

      return this.props.saveUser(newUser).then(reload);
    } else if (user.locale === 'es-US') {
      newUser.locale = 'en-US';

      return this.props.saveUser(newUser).then(reload);
    }

    return reload();
  };

  renderBreadcrumbs({ id, steps }) {
    const {
      route: { allowNavigation, parentPath },
      stepData,
      stepData: { currentStep, pathName },
    } = this.props;

    if (!_.isNumber(currentStep)) {
      return null;
    }

    return (
      <IndicatorWrapperWithRouter
        indicatorClassname="breadcrumb-indicator"
        style={{ position: 'relative' }}
        recalcDuration={230}
        getActiveDomNode={(location) => {
          const currentPath = location.pathname.split('/').pop();
          const crumbId = pathName || currentPath;
          return document.querySelector(`#${id} div[id=${crumbId}]`);
        }}
        getContainingDomWidth={() => getWidth(BC_OUTER_ID)}
        getLeftRight={(domNode) => {
          const isLast = domNode.classList.contains('last');
          return {
            left: 0,
            right: isLast
              ? getWidth(BC_OUTER_ID)
              : domNode.offsetRight || domNode.offsetWidth + domNode.offsetLeft,
          };
        }}>
        {(changeHandler) => {
          const label = stepData.descriptKey;
          const numSteps = _.get(steps, 'length');
          const breadCrumbs = [];

          _.forEach(steps, (step, index) => {
            breadCrumbs.push(
              <Breadcrumb
                active={index === currentStep}
                allowNavigation={allowNavigation}
                clickHandler={changeHandler}
                id={step}
                index={index}
                key={`${id}${index}`}
                last={index === numSteps - 1}
                linkName={label}
                parentPath={parentPath}
                visited={index < currentStep} />,
            );
          });

          return (
            <div className="flex-row expand breadcrumb-container" id={id}>
              {breadCrumbs}
            </div>
          );
        }}
      </IndicatorWrapperWithRouter>
    );
  }

  checkingMXPhoneDisplay(basename, pathname) {
    const {
      phoneNumber,
      partnerConfigDisplayPhoneNumber,
    } = this.props;
    if (phoneNumber && partnerConfigDisplayPhoneNumber) return ['phone'];
    const { phoneWhitelistMap } = this.state;
    return _.includes(mergedPhoneWhitelistPaths, `${basename}${pathname}`) &&
      phoneWhitelistMap[`${basename}${pathname}`]
      ? ['phone']
      : [];
  }

  render() {
    const {
      brandName,
      children,
      location: { basename, pathname },
      route: { steps, className = 'breadcrumbs-padding' },
      user: { locale },
      phoneNumber,
    } = this.props;

    const spanishHeader = _.every(TRANSLATION_SWITCH_PATHS, path => pathname !== path) ? null : (
      <button onClick={this.handleTranslationSwitch} className="accessible-header">
        <Translate className="bold grey-6-color" path={`common.translation.${locale}`} />
      </button>
    );

    let nav = spanishHeader ? (
      <React.Fragment>
        <Nav
          className="home-header-nav"
          byName={this.checkingMXPhoneDisplay(basename, pathname)}
          phoneNumber={phoneNumber} />
        {spanishHeader}
      </React.Fragment>
    ) : (
      <Nav
        className="home-header-nav"
        byName={this.checkingMXPhoneDisplay(basename, pathname)}
        phoneNumber={phoneNumber} />
    );
    nav = <div className="home-header-nav-container">{nav}</div>;

    return (
      <div>
        <a className="skip-main" href="#main-content">
          <Translate path="common.header.skipToContent" />
        </a>
        <StrideHeader brandName={brandName} rightNav={nav} />
        <div className="header-padding">
          <div className="breadcrumbs" id={BC_OUTER_ID}>
            {this.renderBreadcrumbs({
              id: 'bc-container-id',
              steps,
            })}
          </div>
          <div className={className}>{children}</div>
        </div>
      </div>
    );
  }
}

const mapStateToProps: Object = (state) => {
  // display or hide phone number based on contentful config for ichra partner
  // also display custom partner phone number
  // if phoneNumber passed as empty the Nav component would use the default
  // Stride phone number
  let partnerConfigDisplayPhoneNumber = false;
  const partnerConfig = _.get(state, 'siteConfigs.brandSiteConfig.partnerConfig', {});
  const phoneNumber = _.get(partnerConfig, 'agencyInfo.phoneNumber', '');
  const mxPath = _.get(partnerConfig, 'mXPhoneNumberEnablePaths');
  if (mxPath && typeof window !== 'undefined') {
    partnerConfigDisplayPhoneNumber = mxPath.reduce((prev, cur) => prev || window.location.href.includes(cur), false);
  }

  return {
    brandName: state.brand,
    stepData: state.stepData,
    user: state.user,
    phoneNumber,
    partnerConfigDisplayPhoneNumber,
  };
};

export default connect(mapStateToProps, { saveUser })(App);
